import React, { useState, useEffect } from 'react';
import {
  Users,
  UserPlus,
  Pencil,
  Trash2,
  X,
  Check,
  Search,
  Link as LinkIcon,
  AlertCircle,
  KeyRound,
  Mail
} from 'lucide-react';
import { users as userService, organizations } from '../services/api';
import { validateCPF, applyCPFMask } from '../utils/cpfValidator';

const Onboarding = () => {
  const [users, setUsers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  // Whether the credentials email actually went out — drives what the modal claims
  const [emailSent, setEmailSent] = useState(false);
  const [modalMode, setModalMode] = useState('created'); // 'created' | 'reset' | 'resent'
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [notification, setNotification] = useState({ isOpen: false, type: '', message: '' });
  const [cpfError, setCpfError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'patient',
    status: 'active',
    phoneNumber: '',
    cpf: '',
    dateOfBirth: '',
    address: '',
    emergencyContact: '',
    assignedTherapist: '',
    assignedDoctor: '',
    assignedSupervisor: '',
    assignedOperations: ''
  });

  const [availableStaff, setAvailableStaff] = useState({
    therapists: [],
    doctors: [],
    supervisors: [],
    operations: []
  });

  // Super admin org context
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'super_admin';
  const [selectedOrgName, setSelectedOrgName] = useState('');

  // Fetch users and staff on component mount
  useEffect(() => {
    fetchUsers();
    fetchStaffByRole();
    // For super_admin, resolve org name
    if (isSuperAdmin) {
      const orgId = organizations.getSelectedOrgId();
      if (orgId) {
        organizations.getById(orgId)
          .then(res => setSelectedOrgName(res.data.data?.name || ''))
          .catch(() => setSelectedOrgName(''));
      }
    }
  }, []);

  // Helper functions for notifications and confirmations
  const showNotification = (type, message) => {
    setNotification({ isOpen: true, type, message });
    setTimeout(() => {
      setNotification({ isOpen: false, type: '', message: '' });
    }, 4000);
  };

  const showConfirmDialog = (title, message, onConfirm) => {
    setConfirmDialog({ isOpen: true, title, message, onConfirm });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
  };

  const fetchUsers = async () => {
    try {
      console.log('Fetching users...');
      const response = await userService.getAll();
      console.log('Users response:', response);
      if (response?.data?.data) {
        setUsers(response.data.data);
      } else {
        console.error('Invalid response format:', response);
        setUsers([]); // Set empty array as fallback
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]); // Set empty array on error
    }
  };

  const fetchStaffByRole = async () => {
    try {
      const roles = ['therapist', 'doctor', 'supervisor', 'operations'];
      const staffData = {
        therapists: [],
        doctors: [],
        supervisors: [],
        operations: []
      };
      
      for (const role of roles) {
        try {
          const response = await userService.getAll(); // Use the same service as fetchUsers
          const users = response?.data?.data || [];
          staffData[`${role}s`] = users.filter(user => user.role === role);
        } catch (error) {
          console.error(`Error fetching ${role}s:`, error);
        }
      }
      
      setAvailableStaff(staffData);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setAvailableStaff({
        therapists: [],
        doctors: [],
        supervisors: [],
        operations: []
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'cpf') {
      const masked = applyCPFMask(value);
      setFormData(prev => ({ ...prev, cpf: masked }));
      if (masked.replace(/\D/g, '').length === 11) {
        const result = validateCPF(masked);
        setCpfError(result.valid ? '' : result.error);
      } else {
        setCpfError('');
      }
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Clean up the data before sending to API
      const cleanedData = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        status: formData.status,
        phoneNumber: formData.phoneNumber,
        preferences: formData.preferences || {},
        metadata: formData.metadata || {}
      };

      // Add optional fields for patients
      if (formData.role === 'patient') {
        cleanedData.dateOfBirth = formData.dateOfBirth || null;
        cleanedData.address = formData.address || null;
        cleanedData.emergencyContact = formData.emergencyContact || null;
      }

      let response;
      if (isEditing && selectedUser?.id) {
        response = await userService.update(selectedUser.id, cleanedData);
      } else {
        response = await userService.create(cleanedData);
      }

      if (response?.data?.status === 'success') {
        if (isEditing) {
          // Just show success for edits
          showNotification('success', 'Usuário atualizado com sucesso');
        } else {
          // For new users, show the temporary password
          const { user, temporaryPassword, emailSent: emailWasSent } = response.data.data;
          setNewUserEmail(user.email);
          setTemporaryPassword(temporaryPassword);
          setEmailSent(Boolean(emailWasSent));
          setModalMode('created');
          setShowPasswordModal(true);
        }

        // Refresh the users list
        await fetchUsers();

        // Close the add/edit modal
        handleCloseModal();
      } else {
        throw new Error(response?.data?.message || 'Falha ao salvar usuário');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      showNotification('error', error.response?.data?.message || error.message || 'Ocorreu um erro ao salvar o usuário');
    }
  };

  const handleAssignmentSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await userService.updateAssignments(selectedUser.id, {
        therapistId: formData.assignedTherapist,
        doctorId: formData.assignedDoctor,
        supervisorId: formData.assignedSupervisor,
        operationsId: formData.assignedOperations
      });

      if (response?.data?.status === 'success') {
        showNotification('success', 'Atribuições atualizadas com sucesso');
        await fetchUsers();
        setIsAssignModalOpen(false);
      } else {
        throw new Error(response?.data?.message || 'Falha ao atualizar atribuições');
      }
    } catch (error) {
      console.error('Error updating assignments:', error);
      showNotification('error', error.response?.data?.message || error.message || 'Ocorreu um erro ao atualizar as atribuições');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      ...user,
      assignedTherapist: user.assignedTherapist?.id || '',
      assignedDoctor: user.assignedDoctor?.id || '',
      assignedSupervisor: user.assignedSupervisor?.id || '',
      assignedOperations: user.assignedOperations?.id || ''
    });
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleAssign = (user) => {
    setSelectedUser(user);
    setFormData(prev => ({
      ...prev,
      assignedTherapist: user.assignedTherapist?.id || '',
      assignedDoctor: user.assignedDoctor?.id || '',
      assignedSupervisor: user.assignedSupervisor?.id || '',
      assignedOperations: user.assignedOperations?.id || ''
    }));
    setIsAssignModalOpen(true);
  };

  const handleDelete = (userId) => {
    const user = users.find(u => u.id === userId);
    const userName = user ? `${user.firstName} ${user.lastName}` : 'este usuário';

    showConfirmDialog(
      'Confirmar Exclusão',
      `Tem certeza que deseja excluir ${userName}?\n\nEsta ação não pode ser desfeita e todos os dados associados serão permanentemente removidos.`,
      async () => {
        try {
          const response = await userService.delete(userId);

          if (response?.data?.status === 'success') {
            showNotification('success', 'Usuário excluído com sucesso');
            await fetchUsers();
          } else {
            throw new Error(response?.data?.message || 'Falha ao excluir usuário');
          }
        } catch (error) {
          console.error('Error deleting user:', error);

          // Check if it's a dependency error
          if (error.response?.data?.code === 'USER_HAS_DEPENDENCIES') {
            const dependencies = error.response.data.details || [];
            const dependencyList = dependencies.join(', ');
            showNotification('error', `${error.response.data.message}: ${dependencyList}`);
          } else {
            showNotification('error', error.response?.data?.message || error.message || 'Ocorreu um erro ao excluir o usuário');
          }
        }
        closeConfirmDialog();
      }
    );
  };

  const handleResetPassword = (user) => {
    showConfirmDialog(
      'Redefinir Senha',
      `Deseja redefinir a senha de ${user.firstName} ${user.lastName} (${user.email})?\n\nUma nova senha temporária será gerada e o usuário deverá alterá-la no próximo login.`,
      async () => {
        try {
          const response = await userService.resetPassword(user.id);
          if (response?.data?.status === 'success') {
            setNewUserEmail(response.data.data.email);
            setTemporaryPassword(response.data.data.temporaryPassword);
            setEmailSent(Boolean(response.data.data.emailSent));
            setModalMode('reset');
            setShowPasswordModal(true);
          } else {
            throw new Error(response?.data?.message || 'Falha ao redefinir senha');
          }
        } catch (error) {
          console.error('Error resetting password:', error);
          showNotification('error', error.response?.data?.message || error.message || 'Erro ao redefinir senha');
        }
        closeConfirmDialog();
      }
    );
  };

  const handleResendInvite = (user) => {
    showConfirmDialog(
      'Reenviar Convite',
      `Reenviar o convite de acesso para ${user.firstName} ${user.lastName} (${user.email})?\n\nUma nova senha temporária será gerada e a anterior deixará de funcionar.`,
      async () => {
        try {
          const response = await userService.resendInvite(user.id);
          if (response?.data?.status === 'success') {
            setNewUserEmail(response.data.data.email);
            setTemporaryPassword(response.data.data.temporaryPassword);
            setEmailSent(Boolean(response.data.data.emailSent));
            setModalMode('resent');
            setShowPasswordModal(true);
          } else {
            throw new Error(response?.data?.message || 'Falha ao reenviar convite');
          }
        } catch (error) {
          console.error('Error resending invite:', error);
          showNotification('error', error.response?.data?.message || error.message || 'Erro ao reenviar convite');
        }
        closeConfirmDialog();
      }
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setSelectedUser(null);
    setCpfError('');
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      role: 'patient',
      status: 'active',
      phoneNumber: '',
      cpf: '',
      dateOfBirth: '',
      address: '',
      emergencyContact: '',
      assignedTherapist: '',
      assignedDoctor: '',
      assignedSupervisor: '',
      assignedOperations: ''
    });
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(temporaryPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setTemporaryPassword('');
    setNewUserEmail('');
    setPasswordCopied(false);
    setEmailSent(false);
  };

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Render the assignment modal
  const renderAssignModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Assign Staff to Patient</h3>
          <button onClick={() => setIsAssignModalOpen(false)} className="text-gray-400 hover:text-gray-500">
            <X className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleAssignmentSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Assigned Therapist</label>
              <select
                name="assignedTherapist"
                value={formData.assignedTherapist}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                <option value="">Select Therapist</option>
                {availableStaff.therapists.map(therapist => (
                  <option key={therapist.id} value={therapist.id}>
                    {therapist.firstName} {therapist.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Assigned Doctor</label>
              <select
                name="assignedDoctor"
                value={formData.assignedDoctor}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                <option value="">Select Doctor</option>
                {availableStaff.doctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.firstName} {doctor.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Assigned Supervisor</label>
              <select
                name="assignedSupervisor"
                value={formData.assignedSupervisor}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                <option value="">Select Supervisor</option>
                {availableStaff.supervisors.map(supervisor => (
                  <option key={supervisor.id} value={supervisor.id}>
                    {supervisor.firstName} {supervisor.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Assigned Operations Staff</label>
              <select
                name="assignedOperations"
                value={formData.assignedOperations}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
              >
                <option value="">Select Operations Staff</option>
                {availableStaff.operations.map(ops => (
                  <option key={ops.id} value={ops.id}>
                    {ops.firstName} {ops.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setIsAssignModalOpen(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-700 hover:bg-brand-800"
            >
              Save Assignments
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Super Admin Org Context Banner */}
      {isSuperAdmin && (
        <div className="mb-4 px-4 py-3 bg-brand-50 border border-brand-200 rounded-lg flex items-center text-sm text-brand-700">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>
            {selectedOrgName
              ? <>Novos usuários serão criados na organização: <strong>{selectedOrgName}</strong></>
              : <>Nenhuma organização selecionada. Use o seletor de organização na barra lateral.</>
            }
          </span>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Usuários</h1>
          <p className="text-gray-600">Cadastre e administre os usuários da organização</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-4 py-2 bg-brand-700 text-white rounded-lg hover:bg-brand-800"
        >
          <UserPlus className="w-5 h-5 mr-2" />
          Novo Usuário
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-brand-100 flex items-center justify-center">
                        <span className="text-brand-700 font-medium">
                          {user.firstName?.[0]}{user.lastName?.[0]}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.phoneNumber}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{user.role}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(user)}
                    className="text-brand-600 hover:text-brand-900 mr-4"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  {user.role === 'patient' && (
                    <button
                      onClick={() => handleAssign(user)}
                      className="text-brand-600 hover:text-brand-900 mr-4"
                      title="Assign Staff"
                    >
                      <LinkIcon className="w-5 h-5" />
                    </button>
                  )}
                  {user.role !== 'patient' && (
                    <button
                      onClick={() => handleResendInvite(user)}
                      className="text-brand-600 hover:text-brand-900 mr-4"
                      title="Reenviar convite de acesso"
                    >
                      <Mail className="w-5 h-5" />
                    </button>
                  )}
                  {user.role !== 'patient' && (
                    <button
                      onClick={() => handleResetPassword(user)}
                      className="text-amber-600 hover:text-amber-900 mr-4"
                      title="Redefinir Senha"
                    >
                      <KeyRound className="w-5 h-5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(user.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">{isEditing ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-500">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  >
                    <option value="patient">Patient</option>
                    <option value="therapist">Therapist</option>
                    <option value="doctor">Doctor</option>
                    <option value="operations">Operations</option>
                    <option value="admin">Admin</option>
                    <option value="supervisor">Supervisor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  />
                </div>
                {formData.role !== 'patient' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">CPF</label>
                    <input
                      type="text"
                      name="cpf"
                      value={formData.cpf}
                      onChange={handleInputChange}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      className={`mt-1 block w-full rounded-md shadow-sm focus:ring-brand-500 ${
                        cpfError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-brand-500'
                      }`}
                    />
                    {cpfError && (
                      <p className="mt-1 text-sm text-red-600">{cpfError}</p>
                    )}
                  </div>
                )}
                {formData.role === 'patient' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <textarea
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        rows={2}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Emergency Contact</label>
                      <input
                        type="text"
                        name="emergencyContact"
                        value={formData.emergencyContact}
                        onChange={handleInputChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-700 hover:bg-brand-800"
                >
                  {isEditing ? 'Save Changes' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assignment Modal */}
      {isAssignModalOpen && renderAssignModal()}

      {/* Temporary Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-[32rem] shadow-lg rounded-md bg-white">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 bg-green-100 rounded-full p-2 mr-3">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {modalMode === 'created' && 'Usuário Criado com Sucesso!'}
                  {modalMode === 'reset' && 'Senha Redefinida com Sucesso!'}
                  {modalMode === 'resent' && 'Convite Reenviado!'}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Uma senha temporária foi gerada para <strong>{newUserEmail}</strong>
                </p>
              </div>
            </div>

            {/* Say plainly whether the email went out — the admin needs to know
                whether they still have to pass the password on by hand. */}
            {emailSent ? (
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-800">
                      Um e-mail com as instruções de acesso e a senha temporária foi enviado para{' '}
                      <strong>{newUserEmail}</strong>. A senha abaixo é apenas uma cópia, caso precise repassá-la.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>O e-mail não pôde ser enviado.</strong> Copie a senha abaixo e
                      compartilhe com o usuário de forma segura. Esta senha só será exibida uma vez.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Senha Temporária:
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  readOnly
                  value={temporaryPassword}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopyPassword}
                  className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors ${
                    passwordCopied ? 'bg-green-600' : 'bg-brand-700 hover:bg-brand-800'
                  }`}
                >
                  {passwordCopied ? 'Copiado!' : 'Copiar'}
                </button>
              </div>
            </div>

            <div className="bg-brand-50 border border-brand-200 rounded-md p-3 mb-4">
              <p className="text-sm text-brand-800">
                <strong>O que o usuário deve fazer:</strong>
              </p>
              <ol className="text-sm text-brand-700 mt-2 ml-4 list-decimal space-y-1">
                <li>Fazer login com o email e senha temporária</li>
                <li>Será solicitado a criar uma nova senha</li>
                <li>A nova senha deve ter:
                  <ul className="ml-4 mt-1 list-disc">
                    <li>Mínimo 12 caracteres</li>
                    <li>Letras maiúsculas e minúsculas</li>
                    <li>Números e caracteres especiais</li>
                  </ul>
                </li>
              </ol>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleClosePasswordModal}
                className="px-4 py-2 bg-brand-700 text-white rounded-md hover:bg-brand-800 text-sm font-medium"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-[32rem] shadow-lg rounded-md bg-white">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 bg-red-100 rounded-full p-2 mr-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {confirmDialog.title}
                </h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">
                  {confirmDialog.message}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeConfirmDialog}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      {notification.isOpen && (
        <div
          className="fixed top-4 right-4 z-50 transition-all duration-300 ease-in-out"
          style={{ animation: 'slideInRight 0.3s ease-out' }}
        >
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
          <div className={`rounded-lg shadow-lg p-4 max-w-md ${
            notification.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() => setNotification({ isOpen: false, type: '', message: '' })}
                className={`ml-3 flex-shrink-0 ${
                  notification.type === 'success' ? 'text-green-400 hover:text-green-600' : 'text-red-400 hover:text-red-600'
                }`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding; 